/**
 * Jumlah keseluruhan satu jualan.
 *
 * Diskaun peringkat pesanan mesti diagihkan ke baris **sebelum** cukai dikira, supaya
 * resit boleh menunjukkan cukai yang betul bagi setiap baris (ADR-0005 §3). Agihan
 * menggunakan kaedah *largest remainder* supaya jumlah agihan tepat sama dengan diskaun
 * yang diminta — tiada satu sen hilang atau muncul.
 */
import { computeLine } from './line.js';
import { assertSen, roundToNearest5Sen, sumSen } from './money.js';
import { taxFromGross, taxFromNet } from './tax.js';
/**
 * Agihkan `amountSen` kepada `weights` secara berkadar, dengan jumlah yang tepat.
 *
 * `weights` mestilah integer bukan negatif. Jika semua berat sifar, amaun mesti sifar.
 */
export function allocateProportionally(amountSen, weights) {
    assertSen(amountSen, 'amaun agihan');
    if (amountSen < 0) {
        throw new RangeError(`amaun agihan tidak boleh negatif, diterima ${amountSen}`);
    }
    if (weights.length === 0) {
        if (amountSen !== 0) {
            throw new RangeError('tiada berat untuk diagihkan tetapi amaun bukan sifar');
        }
        return [];
    }
    let totalWeight = 0;
    for (const weight of weights) {
        if (!Number.isSafeInteger(weight) || weight < 0) {
            throw new RangeError(`berat mesti integer bukan negatif, diterima ${weight}`);
        }
        totalWeight += weight;
    }
    if (totalWeight === 0) {
        if (amountSen !== 0) {
            throw new RangeError('jumlah berat sifar tetapi amaun bukan sifar');
        }
        return weights.map(() => 0);
    }
    const shares = [];
    const remainders = [];
    let allocated = 0;
    for (const weight of weights) {
        const numerator = amountSen * weight;
        if (!Number.isSafeInteger(numerator)) {
            throw new RangeError('agihan melebihi had selamat integer');
        }
        const share = Math.floor(numerator / totalWeight);
        shares.push(share);
        remainders.push(numerator - share * totalWeight);
        allocated += share;
    }
    let leftover = amountSen - allocated;
    const order = remainders
        .map((remainder, index) => ({ remainder, index }))
        .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    for (const entry of order) {
        if (leftover <= 0)
            break;
        shares[entry.index] = (shares[entry.index] ?? 0) + 1;
        leftover -= 1;
    }
    return shares;
}
export function computeSaleTotals(input) {
    if (input.lines.length === 0) {
        throw new RangeError('jualan mesti mempunyai sekurang-kurangnya satu baris');
    }
    const baseLines = input.lines.map(computeLine);
    const orderDiscount = input.orderDiscountSen ?? 0;
    assertSen(orderDiscount, 'diskaun pesanan');
    if (orderDiscount < 0) {
        throw new RangeError(`diskaun pesanan tidak boleh negatif, diterima ${orderDiscount}`);
    }
    const netSum = sumSen(baseLines.map((line) => line.netSen));
    if (orderDiscount > netSum) {
        throw new RangeError(`diskaun pesanan (${orderDiscount}) melebihi jumlah bersih (${netSum})`);
    }
    const allocation = allocateProportionally(orderDiscount, baseLines.map((line) => line.netSen));
    const adjusted = baseLines.map((line, index) => {
        const allocated = allocation[index] ?? 0;
        const discountSen = line.discountSen + allocated;
        const netSen = line.grossSen - discountSen;
        const rateBp = input.lines[index]?.taxRateBp ?? 0;
        const inclusive = input.lines[index]?.taxInclusive ?? false;
        const taxSen = inclusive ? taxFromGross(netSen, rateBp) : taxFromNet(netSen, rateBp);
        const lineTotalSen = inclusive ? netSen : netSen + taxSen;
        return { grossSen: line.grossSen, discountSen, netSen, taxSen, lineTotalSen };
    });
    const subtotalSen = sumSen(adjusted.map((line) => line.grossSen));
    const discountSen = sumSen(adjusted.map((line) => line.discountSen));
    const taxSen = sumSen(adjusted.map((line) => line.taxSen));
    const unroundedTotal = sumSen(adjusted.map((line) => line.lineTotalSen));
    const mode = input.cashRounding ?? 'none';
    const roundedTotal = mode === 'nearest_5_sen' ? roundToNearest5Sen(unroundedTotal) : unroundedTotal;
    const roundingSen = roundedTotal - unroundedTotal;
    return {
        subtotalSen,
        discountSen,
        taxSen,
        roundingSen,
        totalSen: roundedTotal,
        lines: adjusted,
    };
}
/**
 * Semak identiti yang mesti sentiasa benar. Digunakan dalam ujian dan oleh pekerja
 * malam yang memeriksa integriti data produksi.
 */
export function assertTotalsConsistent(totals) {
    const lineSubtotal = sumSen(totals.lines.map((line) => line.grossSen));
    if (lineSubtotal !== totals.subtotalSen) {
        throw new Error(`subtotal tidak sepadan: ${lineSubtotal} vs ${totals.subtotalSen}`);
    }
    const lineDiscount = sumSen(totals.lines.map((line) => line.discountSen));
    if (lineDiscount !== totals.discountSen) {
        throw new Error(`diskaun tidak sepadan: ${lineDiscount} vs ${totals.discountSen}`);
    }
    const lineTax = sumSen(totals.lines.map((line) => line.taxSen));
    if (lineTax !== totals.taxSen) {
        throw new Error(`cukai tidak sepadan: ${lineTax} vs ${totals.taxSen}`);
    }
    const lineTotal = sumSen(totals.lines.map((line) => line.lineTotalSen));
    const expected = lineTotal + totals.roundingSen;
    if (expected !== totals.totalSen) {
        throw new Error(`jumlah tidak sepadan: ${expected} vs ${totals.totalSen}`);
    }
}
//# sourceMappingURL=totals.js.map